import { ColumnId, SheetColumn, SheetSource } from "./sheetTypes";

/**
 * Reading cell text as a number or a date, for ORDERING AND TYPE DETECTION ONLY.
 * Display and export always use the raw string, which is what keeps "007" seven
 * characters long and stops "1-2" from becoming January 2nd.
 *
 * Both parsers are deliberately strict whitelists. The temptation is to call
 * Number(s) or new Date(s) and let the engine decide; both are far too eager -
 * new Date("1-2") is a valid date in V8 and Number("") is 0.
 */

export type ColumnValueType = "text" | "number" | "date" | "mixed" | "empty";

export type ColumnTypeMap = Record<ColumnId, ColumnValueType>;

/** Blank means "nothing a person typed": empty, or whitespace only. */
export function isBlank(raw: string): boolean {
  return raw == null || raw.trim() === "";
}

const CURRENCY_PREFIX = /^[$\u20AC\u00A3\u00A5\u20BD]\s*/;
const GROUPED_THOUSANDS = /^\d{1,3}(,\d{3})+(\.\d+)?$/;
/**
 * Plain decimal, or an exponent that carries an explicit sign. The sign is
 * required so that an employee code like "5E7" stays text while Excel's own
 * "4.49E+08" is still read as a number.
 */
const PLAIN_NUMBER = /^(?:\d+|\d*\.\d+)(?:[eE][+-]\d+)?$/;

/**
 * "1,234.56" -> 1234.56, "$1,200" -> 1200, "(500)" -> -500, "45%" -> 0.45.
 * Returns null for anything it is not sure about, including "1,2" (which is
 * 1.2 in half of Europe and a typo in the other half).
 */
export function parseNumericCell(raw: string): number | null {
  if (raw == null) return null;
  let s = raw.trim();
  if (!s) return null;

  let negative = false;
  if (s.length > 1 && s.charAt(0) === "(" && s.charAt(s.length - 1) === ")") {
    negative = true;
    s = s.slice(1, -1).trim();
  }
  if (s.charAt(0) === "-") {
    negative = !negative;
    s = s.slice(1).trim();
  } else if (s.charAt(0) === "+") {
    s = s.slice(1).trim();
  }

  s = s.replace(CURRENCY_PREFIX, "");

  let percent = false;
  if (s.charAt(s.length - 1) === "%") {
    percent = true;
    s = s.slice(0, -1).trim();
  }

  if (GROUPED_THOUSANDS.test(s)) {
    s = s.replace(/,/g, "");
  }
  if (!PLAIN_NUMBER.test(s)) return null;

  const value = Number(s);
  if (!isFinite(value)) return null;
  const scaled = percent ? value / 100 : value;
  return negative ? -scaled : scaled;
}

const MONTHS: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const DMY_NAMED = /^(\d{1,2})-([A-Za-z]{3,9})-(\d{4})$/;
const MDY_NAMED = /^([A-Za-z]{3,9})\s+(\d{1,2}),?\s+(\d{4})$/;

function toUtc(year: number, month: number, day: number): number | null {
  if (year < 1000 || year > 9999) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const ms = Date.UTC(year, month - 1, day);
  const back = new Date(ms);
  // Rejects 02/31/2026 and friends instead of silently sliding into March.
  if (back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day)
    return null;
  return ms;
}

/**
 * Epoch milliseconds for the formats this product actually sees, or null.
 * A four digit year or a month name is always required - that is what keeps
 * "1-2" and "007" out.
 */
export function parseDateCell(raw: string): number | null {
  if (raw == null) return null;
  const s = raw.trim();
  if (s.length < 6) return null;

  const iso = ISO_DATE.exec(s);
  if (iso) return toUtc(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const us = US_DATE.exec(s);
  if (us) return toUtc(Number(us[3]), Number(us[1]), Number(us[2]));

  const dmy = DMY_NAMED.exec(s);
  if (dmy) {
    const month = MONTHS[dmy[2].toLowerCase()];
    if (!month) return null;
    return toUtc(Number(dmy[3]), month, Number(dmy[1]));
  }

  const mdy = MDY_NAMED.exec(s);
  if (mdy) {
    const month = MONTHS[mdy[1].toLowerCase()];
    if (!month) return null;
    return toUtc(Number(mdy[3]), month, Number(mdy[2]));
  }

  return null;
}

const HEAD_SAMPLE = 200;
const SPREAD_SAMPLE = 100;
const STRONG_MAJORITY = 0.9;
const MIXED_THRESHOLD = 0.2;

/**
 * Samples the head of the column and, on long sheets, a spread of later rows -
 * a report whose first rows are a preamble should not decide the type alone.
 * Numbers are tested before dates on purpose: "2024" is a number, not a year.
 */
export function detectColumnType(
  source: SheetSource,
  column: SheetColumn,
  sampleSize: number = HEAD_SAMPLE,
): ColumnValueType {
  const rows = source.rows;
  const total = rows.length;
  if (total === 0) return "empty";

  const values: string[] = [];
  for (let i = 0; i < total && values.length < sampleSize; i++) {
    const cell = rows[i].cells[column.sourceIndex];
    if (cell != null && !isBlank(cell)) values.push(cell);
  }
  if (total > 5000) {
    const step = Math.floor(total / SPREAD_SAMPLE) || 1;
    for (
      let i = step;
      i < total && values.length < sampleSize + SPREAD_SAMPLE;
      i += step
    ) {
      const cell = rows[i].cells[column.sourceIndex];
      if (cell != null && !isBlank(cell)) values.push(cell);
    }
  }

  if (values.length === 0) return "empty";

  let numbers = 0;
  let dates = 0;
  for (let i = 0; i < values.length; i++) {
    if (parseNumericCell(values[i]) !== null) {
      numbers++;
    } else if (parseDateCell(values[i]) !== null) {
      dates++;
    }
  }

  const numberShare = numbers / values.length;
  const dateShare = dates / values.length;
  if (numberShare >= STRONG_MAJORITY) return "number";
  if (dateShare >= STRONG_MAJORITY) return "date";
  if (numberShare + dateShare >= STRONG_MAJORITY && numberShare >= dateShare) {
    return "mixed";
  }
  if (numberShare >= MIXED_THRESHOLD || dateShare >= MIXED_THRESHOLD)
    return "mixed";
  return "text";
}

export function detectColumnTypes(source: SheetSource): ColumnTypeMap {
  const types: ColumnTypeMap = {};
  for (let i = 0; i < source.columns.length; i++) {
    const column = source.columns[i];
    types[column.id] = detectColumnType(source, column);
  }
  return types;
}
